/**
 * ProductOrderDialog — 页面**上架商品集合 + 展示顺序**（ADM-PAY-S009，A8 §7.9）
 *
 * 运营在此定「该支付页面卖哪些商品、各排第几位」。保存走**整组替换**
 * （PUT 一次提交有序 `access_level_ids`，数组成员 = 上架、数组位置 = `sort_order`）。
 *
 * 四条会反直觉、但由契约规定的行为，UI 必须如实呈现而不是替运营「纠正」：
 *   - **板块先后固定不可配**：订阅区恒在充值区之前（CLI-RCG-001），故两个板块分开渲染、
 *     不提供跨板块移动；板块内才可调序。
 *   - **未上架 ≠ 从列表消失**：未上架的商品仍在候选表里可见、标「未上架」——运营得先看见它，
 *     才能把它上架。
 *   - **未上架 = 客户端不展示**（适用页面）：C 端只展示本页上架集合；本页一行配置都没有时
 *     该页空态（§3.10「无配置即空集」，不回落全集）。**买断页与运营弹窗页除外** —— 它们不读本表
 *     （见下），故本组件在这两类页上的每一处文案（说明、标注、下架 tooltip、清空确认、保存提示、
 *     页脚）只说「本页配置不生效」，不宣称任何展示后果 —— 包括「空态」这类只对适用页面成立的话。
 *   - **「尚未配置」与「已配置」必须分开呈现**（契约明写前端须据 `is_configured` 分文案）：
 *     配置行全指向已软删商品时两者的 `items[].sort_order` 都全为 null、响应里同形，只有
 *     `is_configured` 能区分 —— 后者仍有残留行，需要「清空配置」入口。
 *   - **可先配置后上架**：`is_active=false` / 不可售的产品同样可进集合，只标注状态、不禁用。
 *
 * **买断页与运营弹窗页不读本表**（§3.10「适用性判据」）：这两类页由外部映射决定每本书 / 每个
 * 弹窗场景卖哪件商品，写入合法但对 C 端零效果。服务端刻意不按 scenario 拒绝，故「配了不生效」
 * 只能由本组件横幅告知 —— 否则运营会在这里配一整页、以为生效了。
 *
 * 排序交互走 `~/ui/SortableList`（拖拽 + 上移 / 下移 + 行尾上架 / 下架），每个板块一个实例：
 * 本组件只负责把板块内的新顺序回填进全局草稿（`refillSortableSlots`），另一板块的相对次序不受影响。
 */

import type {
  PaymentPageProductOrderItem,
  PaymentPageProductOrderResponse,
} from '@novel-admin/shared';
import { onlineManager, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AdminApiError, AdminNetworkError, AdminRequestAbortedError } from '~/api/errors';
import { adaptyProductLinksApi } from '~/hooks/use-adapty-product-links';
import { paymentPagesApi } from '~/hooks/use-payments';
import { refillSortableSlots } from '~/lib/reorder';
import { useAuthStore } from '~/stores/auth-store';
import { Dialog } from '~/ui/Dialog';
import { SortableList } from '~/ui/SortableList';
import { toast } from '~/ui/Toast';
import { PaymentProductScopeHints } from '../PaymentProductScopeHints';

type Props = {
  pageId: string;
  pageName: string;
  onClose: () => void;
  /** 当前支付页 placement 下的 Adapty product_id（来自 catalog）。空数组 = 未同步 / 该 paywall 无产品 */
  placementProductIds: string[];
  isPlacementProductScopeVerified?: boolean;
  /** 该 App id — 取 adapty_product_links 用（product_id ↔ access_level_id 映射） */
  appId: string;
};

/** 板块：SUBSCRIPTION 恒在前（契约固定，不可配） */
const SECTIONS = [
  { type: 'SUBSCRIPTION' as const, label: '订阅产品区', hint: '客户端恒展示在充值区之前' },
  { type: 'CONSUMABLE' as const, label: '充值产品区', hint: '' },
];

/**
 * 不读本表的两类页（§3.10「适用性判据」）：它们的「本页卖哪件商品」由外部映射决定 ——
 * 买断页按 `app_novel_purchase_products`（价格档 → access_level_id），运营弹窗页按
 * `system_popup_access_levels`（场景 → access_level_id）。写入照常成功但 C 端不读，
 * 故两种配置状态都不改变 C 端展示。
 */
const SCENARIOS_NOT_READING_THIS_TABLE = new Set(['novel_purchase', 'operational_popup']);

/** 保存 mutation 的 key：断网放弃那一档要按它精确摘除挂起的提交（见 requestClose）。 */
const SAVE_MUTATION_KEY = (pageId: string) => ['payment-page-product-order-save', pageId] as const;

export function ProductOrderDialog({
  pageId,
  pageName,
  onClose,
  placementProductIds,
  isPlacementProductScopeVerified = false,
  appId,
}: Props) {
  const role = useAuthStore((state) => state.user?.role);
  const qc = useQueryClient();
  const { data, isLoading, isError, fetchStatus, refetch } = useQuery({
    queryKey: ['payment-page-product-order', pageId],
    queryFn: () => paymentPagesApi.getProductOrder(pageId),
    // 全局 staleTime 是 30s，而商品增删改只失效 ['product-metadata', appId]、不碰本 key：
    // 运营改完商品立刻重开本弹窗，会看到缺了新商品、或名称/状态过时的候选表。
    // 弹窗是按需打开的一次性视图，每次进来重取一次代价很小。
    refetchOnMount: 'always',
  });

  /**
   * 「本页 paywall 内」的 access_level_id 集合 —— 用于把候选表按相关性分区。
   *
   * 契约 A8 §7.9 的候选是**该 App 全部** product_metadata（含 is_active=false，供先排序后上架），
   * 服务端口径不动；此处只在**前端**做交叉标注，把与本页 placement 无关的收进折叠区，
   * 避免运营在十几个不相关档位里找本页那几个。
   *
   * 为什么要绕 adapty_product_links：Adapty catalog 只给 product_id（内部 UUID）+ title，
   * **不给 access_level_id**（公开 API 不提供），故必须用 admin 自己的关联表把两者接起来。
   * links 取不到（未关联 / 请求失败）→ 集合为空 → 全部落「其他商品」，不误判成"本页没有商品"。
   */
  const links = useQuery({
    queryKey: ['adapty-product-links', appId],
    queryFn: () => adaptyProductLinksApi.list(appId),
    staleTime: 60_000,
    enabled: role === 'operator' || role === 'admin' || role === 'root',
  });
  const inPaywall = new Set(
    (links.data ?? [])
      .filter((l) => placementProductIds.includes(l.adapty_product_id))
      .map((l) => l.access_level_id),
  );
  // 产品数组不承载完整性证明；可信范围核对由下方独立面板呈现，候选始终完整展示。
  const canFilter =
    isPlacementProductScopeVerified &&
    placementProductIds.length > 0 &&
    placementProductIds.every((id) =>
      (links.data ?? []).some((link) => link.adapty_product_id === id),
    );

  /** 本地草稿：仅保存「已上架」的 access_level_id 有序表；未上架项不在其中 */
  const [ordered, setOrdered] = useState<string[] | null>(null);
  /** 「其它商品」折叠区是否展开（按板块） */
  const [showOthers, setShowOthers] = useState<Record<string, boolean>>({});
  /** 草稿上一次对齐的服务端基线（已上架序列）；用来判断草稿是否被运营改过 */
  const baselineRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (!data) return;
    // 本 effect 依赖 data，而 data 会因重连后台重取、invalidate、重开弹窗时先读缓存再重取等原因刷新。
    //
    // 按「草稿改没改过」分两路，两个方向都不能走极端：
    //   - **没改过**（草稿仍等于上一份基线）→ 直接换成新基线。否则缓存 / 失败前的旧顺序会留在草稿里，
    //     新数据来了也不换，`dirty` 因「旧序 ≠ 新序」变真，零编辑就能点保存、把别人的改动整组写回旧顺序；
    //   - **改过** → 保留运营排好的**幸存者顺序**，只剔掉候选集里已不存在的 id。无条件重置会把他
    //     拖到一半的顺序悄悄冲掉；完全不管则软删商品的 id 留在草稿里、列表上看不见却照样提交，
    //     服务端判 VALIDATION_FAILED，只能关掉弹窗、连带丢掉全部编辑。
    // 服务端已按 (板块序, 未上架在后, sort_order, access_level_id) 排好；草稿只取已上架的那截，
    // 未上架项由 itemsOf 动态追加到板块末尾。
    const nextBaseline = data.items
      .filter((i) => i.sort_order !== null)
      .map((i) => i.access_level_id);
    // 先取出旧基线再更新：setOrdered 的 updater 在下一次渲染时才执行，不能在里面读 ref
    const prevBaseline = baselineRef.current;
    baselineRef.current = nextBaseline;
    const alive = new Set(data.items.map((i) => i.access_level_id));
    setOrdered((prev) => {
      if (prev === null) return nextBaseline;
      const untouched =
        prevBaseline !== null && JSON.stringify(prev) === JSON.stringify(prevBaseline);
      if (untouched) return nextBaseline;
      const survivors = prev.filter((id) => alive.has(id));
      // 无变化时返回原引用：避免每次后台刷新都产生新数组、把 dirty 判定和渲染搅动一遍
      return survivors.length === prev.length ? prev : survivors;
    });
  }, [data]);

  // **卸载也要放弃挂起的提交**：路由后退、PaywallPageDetail 按 key 重挂等路径根本不经过
  // requestClose —— 只在「关窗答是」那一支摘除的话，这些路径下那条 paused mutation 会留在
  // MutationCache 里等联网，然后用断网时刻的旧草稿整组 PUT 回去（覆盖他人改动 + 在已卸载的闭包里
  // 弹「已保存」）。语义统一成「离开即放弃」，与关窗那档一致。
  // **这里不判 failureCount**（requestClose 判它是因为那句确认文案写死了「还没发出去」，得对得上）：
  // 卸载路径没有任何文案要兑现，配了 mutations retry 后「发过一次、失败、再断网」的那条同样是
  // 拿旧草稿整组覆盖，正是本 effect 要防的东西，按 failureCount 放行等于留了半边缺口。
  // 依赖只放 pageId / qc：effect 只在弹窗真正卸载（或换页）时清理，不随每次渲染重挂。
  useEffect(() => {
    return () => {
      for (const mutation of qc
        .getMutationCache()
        .findAll({ mutationKey: SAVE_MUTATION_KEY(pageId), exact: true })) {
        if (mutation.state.isPaused) qc.getMutationCache().remove(mutation);
      }
    };
  }, [qc, pageId]);

  const byId = new Map((data?.items ?? []).map((i) => [i.access_level_id, i]));
  const draft = ordered ?? [];
  const isOrdered = (id: string) => draft.includes(id);

  /** 某板块的展示序列：已上架项按草稿序在前，未上架项按 access_level_id 升序补在后 */
  function itemsOf(type: PaymentPageProductOrderItem['product_type']) {
    const all = (data?.items ?? []).filter((i) => i.product_type === type);
    const configured = draft
      .map((id) => byId.get(id))
      .filter((i): i is PaymentPageProductOrderItem => i?.product_type === type);
    const unconfigured = all
      .filter((i) => !isOrdered(i.access_level_id))
      .sort((a, b) => (a.access_level_id < b.access_level_id ? -1 : 1));
    return [...configured, ...unconfigured];
  }

  /**
   * 把一个板块的条目分成「本页 paywall 内」与「其它」两组。
   *
   * **已上架的一律留在主区**，即便它已不在当前 paywall —— 那是运营的既有配置，藏起来会让人
   * 找不到、也下不掉（同 §草稿幽灵 id 的教训）。只有「未上架 ∧ 不在本页 paywall」才折叠。
   * `canFilter` 为假（links 未加载 / 映射不完整 / placement 未同步）时不折叠任何东西。
   */
  function partition(items: PaymentPageProductOrderItem[]) {
    if (!canFilter) return { primary: items, others: [] as PaymentPageProductOrderItem[] };
    const primary = items.filter(
      (i) => inPaywall.has(i.access_level_id) || isOrdered(i.access_level_id),
    );
    const others = items.filter(
      (i) => !inPaywall.has(i.access_level_id) && !isOrdered(i.access_level_id),
    );
    return { primary, others };
  }

  /**
   * 板块内重排（上移 / 下移 / 拖拽都经 SortableList 汇到这里）：只在同板块的已上架项之间换位，
   * 跨板块不可移（板块先后固定，CLI-RCG-001）。未上架项没有位次，要先上架。
   * 用重排后的同板块序列按原位置回填，另一板块的相对次序不受影响。
   */
  function reorderSection(type: PaymentPageProductOrderItem['product_type'], sectionIds: string[]) {
    setOrdered((prev) => {
      if (!prev) return prev;
      const inSection = (id: string) => byId.get(id)?.product_type === type;
      if (prev.filter(inSection).length !== sectionIds.length) return prev;
      return refillSortableSlots(prev, inSection, sectionIds);
    });
  }

  /** 上架：加入本页集合，位置取该板块已上架项的末尾 */
  function listProduct(accessLevelId: string) {
    setOrdered((prev) => {
      if (!prev || prev.includes(accessLevelId)) return prev;
      const type = byId.get(accessLevelId)?.product_type;
      const lastOfSection = prev.reduce(
        (acc, id, index) => (byId.get(id)?.product_type === type ? index : acc),
        -1,
      );
      const next = [...prev];
      next.splice(lastOfSection + 1, 0, accessLevelId);
      return next;
    });
  }

  /**
   * 下架：移出本页集合 —— 适用页面上该商品保存后**不再进 C 端展示**。
   * 它仍留在候选表里、标「未上架」，运营可随时加回来。
   */
  function unlistProduct(accessLevelId: string) {
    setOrdered((prev) => prev?.filter((id) => id !== accessLevelId) ?? prev);
  }

  /**
   * 本次保存会新增一条 `replace_payment_page_product_order` 审计行，而支付页的「操作日志」
   * 抽屉正查这个 resource。审计查询有全局 30s staleTime：保存前开过抽屉的话，保存后再打开
   * 会命中缓存、看不到刚做的这次变更 —— 运营会以为「没记上」。两条成功路径都要失效。
   */
  function invalidateAuditLogs() {
    void qc.invalidateQueries({ queryKey: ['audit-logs'] });
  }

  /**
   * **提交的 ids 必须是 mutate 的入参、不能读闭包 `draft`**：清空入口要提交 `[]`，而那一刻
   * `draft` 恰恰不是 `[]`（配置行全指向已软删商品时 `draft` 也是 `[]`，但正常态清空时不是）。
   * 读闭包会让「清空」提交成当前草稿、什么也没清掉；onError 的回读比对同理必须比对**本次提交的**
   * ids（useMutation 把它作为第二参给 onError），不是比对渲染时的 draft。
   */
  const save = useMutation({
    // 带 key 才能在「断网挂起时运营选择放弃」那一档精确把这一条从 MutationCache 里摘掉
    // （见 requestClose）——observer 级的 `save.reset()` 摘不掉它，见那里的说明。
    mutationKey: SAVE_MUTATION_KEY(pageId),
    mutationFn: (ids: string[]) =>
      paymentPagesApi.replaceProductOrder(pageId, { access_level_ids: ids }),
    onSuccess: (res: PaymentPageProductOrderResponse, submitted: string[]) => {
      qc.setQueryData(['payment-page-product-order', pageId], res);
      invalidateAuditLogs();
      toast(
        SCENARIOS_NOT_READING_THIS_TABLE.has(res.scenario)
          ? // 这两类页 C 端不读本表：保存成功不等于展示有变化，不能说「生效」或「空态」
            submitted.length === 0
            ? '已清空本页上架配置（本页配置不影响客户端展示）'
            : '已保存（本页配置不影响客户端展示）'
          : submitted.length === 0
            ? // 空数组 = 删光本页行 = C 端该页空态（§3.10「无配置即空集」）
              '已清空本页上架配置：客户端本页不展示任何商品'
            : '已保存：上架集合与顺序即时生效',
        { tone: 'pos', icon: 'IconCheck' },
      );
      onClose();
    },
    onError: async (e: unknown, submitted: string[]) => {
      // **「没拿到响应」≠「没写成」**：PUT 可能已在服务端提交、只是响应丢了（超时 / 断网 / 网关）。
      // 直接报「保存失败」会让运营按旧基线重试，而这次重试夹在他人修改之后就会再覆盖一遍。
      //
      // 判据不能写成 `!(e instanceof AdminApiError)` —— `AdminNetworkError` 与
      // `AdminRequestAbortedError` **都是 AdminApiError 的子类**，那样写这段永远不会执行。
      // 取 `serverStateUnknown`（errors.ts 正为此场景设的：写请求中止后「服务端处理与否不可知」）
      // 并列 `AdminNetworkError`（httpStatus=0、同样没拿到响应）。
      const resultUnknown =
        (e instanceof AdminRequestAbortedError && e.serverStateUnknown) ||
        e instanceof AdminNetworkError;
      if (resultUnknown) {
        try {
          const latest = await paymentPagesApi.getProductOrder(pageId);
          const applied = latest.items
            .filter((i) => i.sort_order !== null)
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .map((i) => i.access_level_id);
          /**
           * **空提交的确认判据不能只比 `applied`**：配置行全部指向已软删商品时，
           * `latest.items` 里本来就没有 `sort_order` 非 null 的项 —— 于是清空**失败**后回读，
           * `applied` 照样是 `[]`、与提交的 `[]` 相等，会被误判成「已在服务端生效」并关掉弹窗，
           * 而那些配置行其实还在、本页仍是「已配置」态。
           *
           * 这个状态恰恰是「清空配置」入口最想支持的那个，故对空提交另要求
           * `is_configured === false`（= 服务端确实没有配置行了）才算确认。
           */
          const confirmed =
            submitted.length === 0
              ? latest.is_configured === false
              : JSON.stringify(applied) === JSON.stringify(submitted);
          if (confirmed) {
            qc.setQueryData(['payment-page-product-order', pageId], latest);
            invalidateAuditLogs();
            // 措辞与正常成功路径同口径：不读本表的页不说「生效」，空提交要说出空态的后果
            toast(
              SCENARIOS_NOT_READING_THIS_TABLE.has(latest.scenario)
                ? '网络异常，但该次保存已写入服务端（已回读确认；本页配置不影响客户端展示）'
                : submitted.length === 0
                  ? '网络异常，但已清空本页上架配置（已回读确认）：客户端本页不展示任何商品'
                  : '网络异常，但该次保存已在服务端生效（已回读确认）',
              { tone: 'pos', icon: 'IconCheck' },
            );
            onClose();
            return;
          }
          // 回读成功但内容不一致 —— 本次确实没写进去，且服务端现状可能已被他人改动，
          // 用最新状态刷新草稿基线，避免拿旧基线重试再覆盖别人。
          // 这里必须**显式** setOrdered：本次提交的草稿与旧基线不同，effect 会按「改过」保留它，
          // 不会自动换成最新基线。
          qc.setQueryData(['payment-page-product-order', pageId], latest);
          setOrdered(applied);
        } catch {
          // 回读也失败（仍断网）：无法判定，按失败报，让运营重开弹窗时看到真实状态
        }
      }
      toast(e instanceof AdminApiError ? e.message : '保存失败，请重试', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  /**
   * 保存进行中禁止直接关闭：PUT 不带 AbortSignal（整组替换一旦到达服务端就会提交，
   * 前端 abort 只能停掉等待、停不掉那次写），关掉弹窗会让运营以为「取消了」，
   * 实际线上排序已被改。故保存期间把关闭收敛成一次确认。
   *
   * **两档的真值都取 MutationCache，不取渲染快照**：`save.isPaused` 是上一次渲染的值，
   * 而网络恢复由 onlineManager 在 microtask 里 continue 掉那条 mutation（`state.isPaused` 当场转 false），
   * React 重渲染要等下一个调度。两处取不同时刻的真值时，会出现「按 paused 档弹了『还没发出去』、
   * 摘除时却复核为已在飞 → 一条都没摘」——运营被告知已放弃，PUT 照样落库。
   */
  function abandonPausedSave(): void {
    // `failureCount === 0` 一并判：`isPaused` **不蕴含**「mutationFn 一次都没跑」—— 配了 mutations
    // retry 之后，第一次 PUT 已发出、失败后断网同样是 paused（failureCount ≥ 1）。今天 app 没配
    // mutations 默认 retry（`lib/query-client.ts` 只配了 queries），但判据写死在这里才不依赖别处。
    const pending = qc
      .getMutationCache()
      .findAll({ mutationKey: SAVE_MUTATION_KEY(pageId), exact: true })
      .filter((m) => m.state.isPaused && m.state.failureCount === 0);
    // **必须从 MutationCache 摘掉，`save.reset()` 不够**：observer 的 reset 只做 `removeObserver`，
    // Mutation 本体仍留在 cache 里且 `state.isPaused` 为真；联网时 QueryClient 订阅 onlineManager 调
    // `resumePausedMutations()`，按 cache 全表取 paused 的逐个 continue() —— 与有没有 observer 无关。
    for (const mutation of pending) qc.getMutationCache().remove(mutation);
  }

  function requestClose() {
    if (!save.isPending) return onClose();
    // 「一次都没发出去」这一档换措辞并真的放弃：运营要的是「我不提交了」，不是「排队等网络」。
    const pausedInCache = qc
      .getMutationCache()
      .findAll({ mutationKey: SAVE_MUTATION_KEY(pageId), exact: true })
      .some((m) => m.state.isPaused && m.state.failureCount === 0);
    if (pausedInCache) {
      const abandon = window.confirm(
        '网络不可用，这次保存还没发出去。关闭将放弃本次提交（不会在联网后自动补发）。确定关闭吗？',
      );
      if (abandon) {
        abandonPausedSave();
        save.reset();
        onClose();
      }
      return;
    }
    const ok = window.confirm(
      '保存请求已发出，关闭窗口不会取消它 —— 该次改动仍可能写入服务端。确定关闭吗？',
    );
    if (ok) onClose();
  }

  /**
   * 买断页 / 运营弹窗页不读本表（§3.10「适用性判据」）：这两类页上的配置不对应任何展示后果，
   * 本组件所有文案在它们上面只说「本页配置不生效」（见文件头）。
   */
  const notReadingThisTable =
    data !== undefined && SCENARIOS_NOT_READING_THIS_TABLE.has(data.scenario);

  const dirty =
    data !== undefined &&
    ordered !== null &&
    JSON.stringify(ordered) !==
      JSON.stringify(data.items.filter((i) => i.sort_order !== null).map((i) => i.access_level_id));

  /**
   * 「清空配置」必须是**独立入口、不受 `dirty` 约束**。
   *
   * `dirty` 与草稿初始化用的是同一个 `filter(sort_order !== null)` 表达式，于是当本页配置行
   * **全部指向已软删商品**时（`is_configured=true` 而 `effective_product_count=0`），两边恒为
   * `[]`、`dirty` 恒假、保存按钮永久禁用 —— 运营既清不掉残留的失效配置行，也没有任何别的
   * 路径提交 `[]`（先上架再下架只会绕回同一状态）。而这恰恰是最需要清空的状态。
   *
   * 故按 `is_configured` 给出显式出口：有配置行就能一键清空（C 端该页空态）。
   */
  // 后台重取失败时 TanStack 保留旧 data 但主体只显示失败面板：此时写入口一律关掉，
  // 否则运营会按一份看不见的旧基线整组替换（失败面板也据此承诺「取数成功前不可保存」）
  const canClearConfig = data?.is_configured === true && !save.isPending && !isError;

  /**
   * 提交空集合的**唯一**确认文案。
   *
   * 两条路径都会到达「提交 `[]`」这个终态 —— 点「清空配置」，或把最后一个已上架商品逐个下架后
   * 保存。契约语义是 `[]` = 删光本页行 = **C 端该页空态**（§3.10「无配置即空集」）：
   * 本页一个商品都不卖，故两条路径共用同一道确认，不能只给「清空配置」加。
   */
  function confirmEmptySubmit(): boolean {
    // 纯文本对话框，不渲染 markdown —— 强调靠措辞与换行，别写星号
    if (notReadingThisTable) {
      return window.confirm(
        '将清空本页全部上架配置。\n\n' +
          '这类页面卖哪件商品由外部映射决定，客户端不读本页配置：清空对客户端展示没有任何影响。确定继续吗？',
      );
    }
    return window.confirm(
      '本页将没有任何上架商品。\n\n' + '没有配置行 = 客户端本页空态，不展示任何商品。确定继续吗？',
    );
  }

  /**
   * 未上架项的标注。**有未保存的改动时说「保存后」的后果，否则说当前生效的后果** —— 标注挂在
   * 草稿成员上，只看服务端 `is_configured` 会在两种编辑中途说反：
   * - 未配置页上架了一个子集：其余商品当前确实展示，但一保存本页就成了「已配置」、它们不再展示；
   * - 已配置页把草稿清空：保存 `[]` 后本页空态，全部商品都不展示。
   */
  const unlistedNote = notReadingThisTable
    ? ' · 未上架（本页配置不生效）'
    : dirty
      ? draft.length > 0
        ? ' · 未上架（保存后客户端不展示）'
        : ' · 未上架（保存后本页空态）'
      : data?.is_configured
        ? ' · 未上架（客户端不展示）'
        : ' · 未上架（本页尚未配置，客户端空态）';

  /** 点击时同步判断，不让离线点击进入稍后可观察到重连状态的mutation微任务。 */
  function rejectOfflineSave() {
    if (onlineManager.isOnline()) return false;
    toast('网络不可用，本次保存未发送；恢复后请重新确认并保存。', {
      tone: 'neg',
      icon: 'IconAlert',
    });
    return true;
  }

  function clearConfig() {
    if (rejectOfflineSave()) return;
    if (confirmEmptySubmit()) save.mutate([]);
  }

  /**
   * 保存：草稿为空且本页当前已配置时，等价于清空 —— 走同一道确认（见 `confirmEmptySubmit`）。
   * 本页原本就未配置（`is_configured=false`）时草稿为空是无变化态，`dirty` 为假、走不到这里。
   */
  function handleSave() {
    if (rejectOfflineSave()) return;
    if (draft.length === 0 && data?.is_configured === true) {
      if (!confirmEmptySubmit()) return;
    }
    save.mutate(draft);
  }

  return (
    <Dialog
      title={`上架商品与顺序 · ${pageName}`}
      onClose={requestClose}
      width={720}
      footer={
        <>
          <span style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            {/* 取数前不知道是哪类页，不下任何结论 */}
            {data === undefined || isError
              ? null
              : notReadingThisTable
                ? '本页配置不影响客户端展示'
                : '保存即时生效，不需要重新上架页面'}
          </span>
          {canClearConfig ? (
            <button
              className="btn btn-ghost"
              title={
                notReadingThisTable
                  ? '删光本页配置行（本页配置不影响客户端展示）'
                  : '删光本页配置行，客户端本页空态'
              }
              onClick={clearConfig}
            >
              清空配置
            </button>
          ) : null}
          <button className="btn" onClick={requestClose}>
            取消
          </button>
          <button
            className="btn btn-primary"
            disabled={!dirty || save.isPending || isError}
            onClick={handleSave}
          >
            {save.isPaused ? '等待网络…' : save.isPending ? '保存中…' : '保存'}
          </button>
        </>
      }
    >
      {/* Dialog 壳 body 不带内边距，由弹窗自带；与「新建 App」等弹窗同一套 18px 左右边距 */}
      <div style={{ padding: '14px 18px 18px' }}>
        {isError && fetchStatus === 'idle' ? (
          // 判 `fetchStatus === 'idle'` 而不是 `!isFetching`：断网时点「重试」，TanStack 把这次重取挂成
          // `fetchStatus='paused'`（`isFetching` 仍为 false），只看 isFetching 会让界面停在同一张失败面板 ——
          // 运营点了没有任何反馈，只能反复点。paused 归到下方分支，并明说在等网络。
          // **差别只在「缓存里已有 data」那一档**：首屏取数失败时 `data === undefined`，TanStack 会把
          // status 重置成 pending、isError 当场变 false，新旧判据行为相同；后台重取失败（旧 data 还在）
          // 才会停在 isError=true，那正是本判据要修的场景。
          // 取数失败时 data 为 undefined、isLoading 也已为 false：不单独分支的话，下面会照常
          // 渲染编辑器、各板块因 items 为空而全部消失 —— 呈现成「该 App 一个商品都没有」。
          // 那是把查询故障伪装成业务事实，运营可能据此以为商品配置丢了。
          <div style={{ padding: 'var(--gap)' }}>
            <div style={{ color: 'var(--neg)', fontSize: 'var(--text-sm)', fontWeight: 500 }}>
              商品列表加载失败
            </div>
            <div
              className="muted"
              style={{ fontSize: 'var(--text-sm)', marginTop: 'calc(var(--gap) * 0.5)' }}
            >
              这不代表该 App 没有商品。请重试；取数成功前本弹窗不可保存。
            </div>
            <button
              className="btn btn-sm"
              style={{ marginTop: 'var(--gap)' }}
              onClick={() => void refetch()}
            >
              重试
            </button>
          </div>
        ) : isLoading || data === undefined || isError ? (
          // 三种情形都显示加载中：① 首次取数；② 断网时查询停在 paused（isLoading 与 isError 都为 false、
          // data 仍 undefined），落进编辑器会把「还没取到」渲染成「该 App 暂无商品」；③ 失败后正在重试
          // （isError 仍为真，fetchStatus 为 fetching 或 paused）—— 继续显示失败面板会让「在重试」与
          // 「没重试」看起来一样
          <div className="muted">
            {fetchStatus === 'paused' ? '网络不可用 · 恢复后自动重试…' : '加载中…'}
          </div>
        ) : (
          <div className="vstack" style={{ gap: 12 }}>
            {data && (
              <PaymentProductScopeHints
                appId={appId}
                scenario={data.scenario}
                platform="IOS"
                items={data.items}
                selectedAccessLevelIds={draft}
              />
            )}
            {/**
             * 「尚未配置·空态」与「已配置·只展示这几个」必须分文案（§7.9 明写）。判据取
             * is_configured，不能拿「有没有项带 sort_order」推 —— 配置行指向的商品被软删时后者会误判。
             */}
            {data && notReadingThisTable ? (
              // 不读本表的页：只陈述配置状态，展示后果交给下方横幅（「本页的配置不会生效」）
              <p
                className="muted"
                style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6, margin: 0 }}
              >
                {data.is_configured ? (
                  <>
                    本页已配置 <strong>{data.effective_product_count}</strong> 个上架商品。
                  </>
                ) : (
                  '本页尚未配置上架商品。'
                )}
              </p>
            ) : data && !data.is_configured ? (
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 'calc(var(--gap) * 0.7) var(--gap)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)',
                  background: 'var(--bg-subtle)',
                }}
              >
                <strong>本页尚未配置上架商品</strong> —— 客户端本页空态，不展示任何商品。
                上架任意一个即成为「已配置」。
              </p>
            ) : data ? (
              <p
                className="muted"
                style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6, margin: 0 }}
              >
                本页已上架 <strong>{data.effective_product_count}</strong> 个商品，按下列顺序排列。
                客户端实际展示的是其中通过可售状态与发现面归属过滤后的那些，
                <strong>不等于这个数</strong>。清空全部上架商品会让客户端本页空态。
              </p>
            ) : null}
            {/**
             * 这两类页的「卖哪件商品」由外部映射决定、C 端不读本表（§3.10 适用性判据）。服务端
             * 刻意不按 scenario 拒绝写入，所以只有这里能告知运营「配了不生效」—— 少了这条横幅，
             * 运营会在买断页配一整页顺序，线上毫无变化又查不出原因。
             */}
            {data && SCENARIOS_NOT_READING_THIS_TABLE.has(data.scenario) ? (
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  lineHeight: 1.6,
                  margin: 0,
                  padding: 'calc(var(--gap) * 0.7) var(--gap)',
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--warn)',
                  background: 'var(--bg-subtle)',
                }}
              >
                <strong>本页的配置不会生效。</strong>
                {data.scenario === 'novel_purchase'
                  ? '买断页卖哪件商品由「价格档 → 买断产品」映射决定（按书籍 price_level 取），客户端不读本页配置。'
                  : '运营弹窗页卖哪件商品由「弹窗场景 → access level」映射决定，客户端不读本页配置。'}
                此处仍可保存，但对客户端展示无任何影响。
              </p>
            ) : null}
            <p className="muted" style={{ fontSize: 'var(--text-xs)', lineHeight: 1.6, margin: 0 }}>
              <strong>订阅区恒在充值区之前</strong>，板块先后不可调整；板块内可调序。
              未上架的商品仍列在该板块末尾供随时上架。
            </p>
            {SECTIONS.map((section) => {
              const allItems = itemsOf(section.type);
              const { primary, others } = partition(allItems);
              const items = primary;
              if (allItems.length === 0) return null;
              return (
                <section key={section.type} style={{ marginTop: 4 }}>
                  <div className="label" style={{ marginBottom: 6 }}>
                    {section.label}
                    {/* hint 是 C 端展示口径的说明，不读本表的页不适用 */}
                    {section.hint && !notReadingThisTable ? (
                      <span
                        className="muted"
                        style={{
                          fontWeight: 400,
                          marginLeft: 'calc(var(--gap) * 0.7)',
                          textTransform: 'none',
                          letterSpacing: 0,
                        }}
                      >
                        · {section.hint}
                      </span>
                    ) : null}
                  </div>
                  <SortableList<PaymentPageProductOrderItem>
                    items={items}
                    getKey={(item) => item.access_level_id}
                    itemLabel={(item) => item.name}
                    // 只有已上架项有位次、可拖可上下移：未上架项要先上架
                    isSortable={(item) => isOrdered(item.access_level_id)}
                    // 保存进行中冻结排序与上下架：mutationFn 提交的是**发起那一刻**闭包里的 draft，
                    // 之后的调整不会进这次请求，成功即 onClose —— 运营的改动被静默丢弃
                    disabled={save.isPending}
                    endDropLabel="放到本区末尾"
                    onReorder={(next) =>
                      reorderSection(
                        section.type,
                        next
                          .filter((i) => isOrdered(i.access_level_id))
                          .map((i) => i.access_level_id),
                      )
                    }
                    renderContent={(item) => (
                      <>
                        <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>
                          {item.name}
                        </div>
                        <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}>
                          <code className="mono">{item.access_level_id}</code>
                          {!isOrdered(item.access_level_id) && unlistedNote}
                          {/* 已上架但暂不可售：契约允许「先配置后上架」，此处只标注不禁用 */}
                          {!item.is_serveable && ' · 暂不可售'}
                          {item.is_in_discovery_scope === false &&
                            ' · 不属于本页发现面，C 端不展示'}
                        </div>
                      </>
                    )}
                    renderActions={(item) =>
                      isOrdered(item.access_level_id) ? (
                        <button
                          className="btn btn-sm btn-ghost"
                          title={
                            // 不读本表的两类页不宣称任何展示后果（先判，最后一个也不例外）；
                            // 适用页面移出最后一个 = 提交空集合 = 本页空态，故在那一刻把后果
                            // 写进 tooltip（保存时另有一道确认兜底）
                            notReadingThisTable
                              ? '移出本页上架集合（本页配置不影响客户端展示）'
                              : items.filter((i) => isOrdered(i.access_level_id)).length === 1 &&
                                  draft.length === 1
                                ? '移出本页上架集合 —— 这是最后一个已上架商品，保存后客户端本页空态'
                                : '移出本页上架集合（保存后客户端不再展示）'
                          }
                          disabled={save.isPending}
                          onClick={() => unlistProduct(item.access_level_id)}
                        >
                          下架
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm"
                          title="上架到本页"
                          disabled={save.isPending}
                          onClick={() => listProduct(item.access_level_id)}
                        >
                          上架
                        </button>
                      )
                    }
                  />
                  {others.length > 0 ? (
                    <div style={{ marginTop: 'calc(var(--gap) * 0.7)' }}>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={() =>
                          setShowOthers((prev) => ({
                            ...prev,
                            [section.type]: !prev[section.type],
                          }))
                        }
                      >
                        {showOthers[section.type] ? '收起' : '展开'}其它商品（{others.length}）
                      </button>
                      <span
                        className="muted"
                        style={{ marginLeft: 'calc(var(--gap) * 0.7)', fontSize: 'var(--text-xs)' }}
                      >
                        不在本页 Adapty paywall 内 —— 仍可上架（先配置后上架）
                      </span>
                      {showOthers[section.type] ? (
                        <ol
                          className="card-flat"
                          style={{
                            listStyle: 'none',
                            margin: '6px 0 0',
                            padding: 0,
                          }}
                        >
                          {others.map((item, index) => (
                            <li
                              key={item.access_level_id}
                              className="hstack"
                              style={{
                                padding: 'calc(var(--gap) * 0.7) var(--gap)',
                                borderTop: index === 0 ? undefined : '1px solid var(--border)',
                                borderRadius: 'var(--radius-sm)',
                                opacity: 0.75,
                              }}
                            >
                              <span
                                className="muted mono"
                                style={{
                                  width: 20,
                                  textAlign: 'right',
                                  fontSize: 'var(--text-xs)',
                                }}
                              >
                                —
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>
                                  {item.name}
                                </div>
                                <div
                                  className="muted"
                                  style={{ fontSize: 'var(--text-xs)', marginTop: 2 }}
                                >
                                  <code className="mono">{item.access_level_id}</code> · 不在本页
                                  paywall
                                  {!item.is_serveable && ' · 暂不可售'}
                                  {item.is_in_discovery_scope === false &&
                                    ' · 不属于本页发现面，C 端不展示'}
                                </div>
                              </div>
                              <button
                                className="btn btn-sm"
                                title="上架到本页"
                                disabled={save.isPending}
                                onClick={() => listProduct(item.access_level_id)}
                              >
                                上架
                              </button>
                            </li>
                          ))}
                        </ol>
                      ) : null}
                    </div>
                  ) : null}
                </section>
              );
            })}
            {(data?.items.length ?? 0) === 0 && (
              <div className="muted">该 App 暂无商品，请先在「支付产品」中配置。</div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
